import React, { useEffect, useRef } from 'react';

// `inert` saca el checkbox del foco y del árbol de accesibilidad; `aria-hidden` y `tabIndex={-1}`
// quedan de respaldo donde no hay `inert`. Va como cadena vacía y con aserción porque React 17 no
// lo reconoce como booleano y sus tipos no lo incluyen.
const DECORATIVE_WRAPPER_PROPS = { 'aria-hidden': true, inert: '' } as React.HTMLAttributes<HTMLDivElement>;

type InputCheckboxProps = {
  checked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  /** Solo muestra el estado, dentro de otro control que ya lo anuncia (p. ej. un `role="option"`). */
  isDecorative?: boolean;
  label?: React.ReactNode;
  labelPosition?: 'before' | 'after';
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  subtitle?: React.ReactNode;
  tabIndex?: number;
  value?: string;
  variant?: 'check' | 'square';
};

const InputCheckbox = React.forwardRef<HTMLDivElement, InputCheckboxProps>(
  (
    {
      checked = false,
      disabled = false,
      indeterminate = false,
      isDecorative = false,
      label = '',
      labelPosition = 'after',
      onChange = () => {},
      subtitle = '',
      tabIndex,
      value,
      variant = 'check',
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    return (
      <div
        className={`input-checkbox input-checkbox--${variant}${labelPosition === 'before' ? ' input-checkbox--label-before' : ''}`}
        ref={ref}
        {...(isDecorative ? DECORATIVE_WRAPPER_PROPS : {})}
      >
        <label className="input-checkbox__label-wrapper">
          <input
            ref={inputRef}
            type="checkbox"
            className="input-checkbox__input"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            tabIndex={isDecorative ? -1 : tabIndex}
            value={value}
            {...props}
          />
          <span className="input-checkbox__box" aria-hidden="true" />
          <div className="input-checkbox__texts">
            <span className="input-checkbox__label">{label}</span>
            {subtitle && <span className="input-checkbox__subtitle">{subtitle}</span>}
          </div>
        </label>
      </div>
    );
  }
);

InputCheckbox.displayName = 'InputCheckbox';

export default InputCheckbox;
